import { Router } from '@angular/router';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';

import { Observable, Subscription } from 'rxjs';

import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import * as moment from 'moment-timezone';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, AbstractControl } from '@angular/forms';


import { PurchaseContentDaily } from '../firestore-models/purchase-content-daily';
import { Facility } from '../firestore-models/facility';
import { Hall } from '../firestore-models/hall';
import { ProjectLanguage } from '../firestore-models/project-language';

import { ConfigurationService } from '../configuration.service';
import { AppConst } from '../app-const';

import { SelectPurchaseContentDailyDialogComponent } from './select-purchase-content-daily-dialog/select-purchase-content-daily-dialog.component';
import { TranslateService } from '@ngx-translate/core';


@Component({
  selector: 'app-purchase-content-daily',
  templateUrl: './purchase-content-daily.component.html',
  styleUrls: ['./purchase-content-daily.component.css'],
  standalone: false,
})
export class PurchaseContentDailyComponent implements OnInit, OnDestroy {

  // コレクション
  private hallsCollection!: AngularFirestoreCollection<Hall>;
  hallsObservable!: Observable<Hall[]>;
  hallsSubscribe!: Subscription;
  private purchaseContentDailiesCollection!: AngularFirestoreCollection<PurchaseContentDaily>;
  purchaseContentDailiesObservable!: Observable<PurchaseContentDaily[]>;
  purchaseContentDailiesSubscribe!: Subscription;
  private facilitiesCollection!: AngularFirestoreCollection<Facility>;
  facilitiesObservable!: Observable<Facility[]>;
  facilitiesSubscribe!: Subscription;

  //各コンテンツ購入日次情報に紐付くプロジェクトID
  projectId: string;
  //各コンテンツ購入日次情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  //コンテンツ購入日次情報テーブル
  displayedColumns: string[] = ['position', 'facility_name', 'name', 'date', 'count', 'price', 'preview'];
  dataSource = new MatTableDataSource<PurchaseContentDaily>();
  selection = new SelectionModel<PurchaseContentDaily>(true, []);

  facilities!: Array<Facility>;
  halls!: Array<Hall>;

  searchFilter: any = {};

  searchParameter = {
    facility_id: null,
    hall_id: null,
    start_date: null,
    end_date: null,
    limit: 15,
    index: 0,
    doc: null,
    prev_doc: null,
    next_doc: null,
    next: true,
  } as any;
  isProcessing = false;

  editForm: UntypedFormGroup;
  get pagesize(): AbstractControl { return this.editForm.get('pagesize')!; }

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;


  constructor(
    private router: Router,
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private formBuilder: UntypedFormBuilder,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
    moment.tz.setDefault('Asia/Tokyo');

    this.editForm = this.formBuilder.group({
      pagesize  : new UntypedFormControl({value: '15',  disabled: false}, []),
    });

  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    // 施設取得
    this.facilitiesCollection = this.angularFirestore.collection<Facility>(
      AppConst.FIRESTORE_FACILITIES, ref => ref.where('project_id', '==', this.projectId).orderBy('facility_id', 'asc'));
    this.facilitiesObservable = this.facilitiesCollection.valueChanges({ idField: 'id' });
    this.facilitiesSubscribe = this.facilitiesObservable.subscribe(facilities => {
      this.facilities = facilities;
    });

    // 建物・カテゴリ情報取得
    this.hallsCollection = this.angularFirestore.collection<Hall>(
      AppConst.FIRESTORE_HALLS, ref => ref.where('project_id', '==', this.projectId).orderBy('hall_id', 'asc'));
    this.hallsObservable = this.hallsCollection.valueChanges({ idField: 'id' });
    this.hallsSubscribe = this.hallsObservable.subscribe(halls => {
      this.halls = halls;
    });

    // コンテンツ購入日次情報取得
    this.getPurchaseContentDailies();

    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.hallsSubscribe.unsubscribe();
    this.purchaseContentDailiesSubscribe.unsubscribe();
    this.facilitiesSubscribe.unsubscribe();
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string | boolean, b: number | string | boolean, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'facility_name':
          return compare(a.facility_id, b.facility_id, isAsc);
        case 'name':
          return compare(a.hall_id, b.hall_id, isAsc);
        case 'date':
          return compare(a.date.seconds, b.date.seconds, isAsc);
        case 'count':
          return compare(a.count, b.count, isAsc);
        case 'price':
          return compare(a.price, b.price, isAsc);
        case 'preview':
          return compare(a.preview, b.preview, isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      let facilityName = '';
      let hallName = '';
      let preview = '';
      for (let facility of this.facilities) {
        if (doc.facility_id === facility.id) {
          facilityName = facility.facility_names[0].name;
        }
      }
      for (let hall of this.halls) {
        if (doc.hall_id === hall.id) {
          hallName = hall.hall_names[0].name;
        }
      }
      if (doc.preview === false) {
        preview = this.translate.instant('本番');
      } else if (doc.preview === true) {
        preview = this.translate.instant('プレ');
      }

      filterValue += facilityName + ' ' + hallName + ' ' + preview;
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  openSearch(): void {
    const dialogRef = this.dialog.open(SelectPurchaseContentDailyDialogComponent, {
      width: '640px',
      disableClose: true,
      data: this.searchParameter
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        console.log(result);
        this.searchParameter.facility_id = result.facility_id;
        this.searchParameter.hall_id = result.hall_id;
        this.searchParameter.start_date = result.start_date;
        this.searchParameter.end_date = result.end_date;
        this.searchParameter.index = 0;
        this.searchParameter.doc = null;
        this.searchParameter.next = true;
        this.getPurchaseContentDailies();
      }
    });
  }

  async getPurchaseContentDailies(): Promise<void> {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;    

    if (!!this.purchaseContentDailiesSubscribe) {
      this.purchaseContentDailiesSubscribe.unsubscribe();
    }
    this.purchaseContentDailiesCollection = this.angularFirestore.collection<PurchaseContentDaily>(AppConst.FIRESTORE_PURCHASE_CONTENT_DAILIIES, ref => 
    {
      let query: any = ref;
      query = query.where('project_id', '==', this.projectId);
      if (!!this.searchParameter.facility_id) { 
        query = query.where('facility_id', '==', this.searchParameter.facility_id);
      };
      if (!!this.searchParameter.hall_id) { 
        let hallId = this.searchParameter.hall_id;
        // 検索条件の建物・カテゴリIDは項目のIDなので、ドキュメントIDに変換
        for (const hall of this.halls) {
          if (hall.hall_id === hallId) {
            hallId = hall.id;
            break;
          }
        }
        query = query.where('hall_id', '==', hallId);
      };

      if (!!this.searchParameter.start_date && !!this.searchParameter.end_date) { 
        const startDate = moment(this.searchParameter.start_date, 'YYYYMMDD').toDate();
        const endDate = moment(this.searchParameter.end_date, 'YYYYMMDD').toDate();
        query = query.where('date', '>=', startDate);
        query = query.where('date', '<=', endDate);
      };
      query = query.orderBy('date', 'desc');
      query = query.orderBy('hall_id', 'asc');
      if (!!this.searchParameter.doc) {
        if (this.searchParameter.next) {
          query = query.startAfter(this.searchParameter.doc);
        } else {
          query = query.endBefore(this.searchParameter.doc);
        }
      }
      if (this.searchParameter.next) {
        query = query.limit(this.searchParameter.limit + 1);
      } else {
        query = query.limitToLast(this.searchParameter.limit + 1);
      }
      return query;
    });

    this.purchaseContentDailiesObservable = this.purchaseContentDailiesCollection.valueChanges({ idField: 'id' });
    this.purchaseContentDailiesSubscribe = this.purchaseContentDailiesObservable.subscribe(async dailies => {

      // 次回検索開始位置を保持
      this.searchParameter.next_doc = null;
      if (dailies.length > 0) {
        if (this.searchParameter.next) {
          const subPrev = this.angularFirestore.collection(AppConst.FIRESTORE_PURCHASE_CONTENT_DAILIIES).doc(dailies[0].id).get().subscribe(doc => {
            subPrev.unsubscribe();
            this.searchParameter.prev_doc = doc;
          });
          if (dailies.length > this.searchParameter.limit) {
            const subNext = this.angularFirestore.collection(AppConst.FIRESTORE_PURCHASE_CONTENT_DAILIIES).doc(dailies[this.searchParameter.limit - 1].id).get().subscribe(doc => {
              subNext.unsubscribe();
              this.searchParameter.next_doc = doc;
            });
          }
        } else {
          if (dailies.length > this.searchParameter.limit) {
            const subPrev = this.angularFirestore.collection(AppConst.FIRESTORE_PURCHASE_CONTENT_DAILIIES).doc(dailies[1].id).get().subscribe(doc => {
              subPrev.unsubscribe();
              this.searchParameter.prev_doc = doc;
            });
          } else {
            this.searchParameter.prev_doc = null;
          }
          const subNext = this.angularFirestore.collection(AppConst.FIRESTORE_PURCHASE_CONTENT_DAILIIES).doc(dailies[dailies.length - 1].id).get().subscribe(doc => {
            subNext.unsubscribe();
            this.searchParameter.next_doc = doc;
          });
        }
        dailies = dailies.slice(0, this.searchParameter.limit); 
      } else {
        this.searchParameter.index = 0;
        this.searchParameter.doc = null;
        this.searchParameter.prev_doc = null;
      }
      this.dataSource.data = this.addFilterValue(dailies);

      this.isProcessing = false;    
    });
  
  }

  // ページサイズ変更
  onPageSizeChange(event: any) {
    this.searchParameter.limit = Number(this.pagesize.value)
    this.searchParameter.index = 0;
    this.searchParameter.doc = null;
    this.searchParameter.next = true;
    this.getPurchaseContentDailies();
  }

  // 前へ
  onPrevClick() {
    this.searchParameter.index--;
    this.searchParameter.doc = this.searchParameter.prev_doc;
    this.searchParameter.next = false;
    this.getPurchaseContentDailies();
  }

  // 次へ
  onNextClick() {
    this.searchParameter.index++;
    this.searchParameter.doc = this.searchParameter.next_doc;
    this.searchParameter.next = true;
    this.getPurchaseContentDailies();
  }}
